import { createQueryString, getQueryParam, isNonEmptyString, requestHtml } from '@vfde-sails/utils';
import { all, call, put, select } from 'redux-saga/effects';
import { getCMSConfig } from '../../util/cms';
import { AdditionalPageOptions } from '../../interface';
import { Overlay } from '@vfde-brix/ws10/overlay';
import {
    setOverlayLoaded,
    setProductDetails,
    setSelectedGigakombi,
    setSelectedProduct,
} from './action';
import {
    getProductDetails,
    JarvisProductGroup,
    ProductDetailsResponse,
    ProductFilter,
    initAvailability as initAvailabilitySaga,
    AvailabilityCheckResponse,
    selectAvailabilityResult,
} from '@vfde-sails/availability';
import { Gigakombi, QueryNames } from '../../enum';
import { selectIsGigakombi, selectProductConfig, selectSelectedId } from './selector';
import { ProductConfig } from './interface';
import overlayUtils from '../../util/component/overlay.utils';
import { Customer, selectCustomer } from '@vfde-sails/login';
import { trackCustomerNotification, trackForm, trackPageView } from '../../tracking/generator';
import { selectIsBookingDisabled, selectIsNotMonthlyBilling, selectIsTvCustomer } from '../AddressValidation/selector';

export function* initStateFromQuery () {
    const metacode: string | null = yield call(getQueryParam, QueryNames.PRODUCT);
    const gigakombi: string | null = yield call(getQueryParam, QueryNames.GIGAKOMBI);
    const productConfig: ProductConfig = yield select(selectProductConfig);

    if (isNonEmptyString(metacode) && productConfig[metacode]) {
        yield put(setSelectedProduct(productConfig[metacode], false));
    }

    if (isNonEmptyString(gigakombi)) {
        yield put(setSelectedGigakombi(Gigakombi.INTERNET, false));
    }
}

export function* initOverlay (overlay: Overlay | null) {
    const config: AdditionalPageOptions = yield call(getCMSConfig);

    if (!config || !overlay) {
        return;
    }

    const results: string[] = yield all([
        call(overlayUtils.getLoadingSpinnerHtml),
        call(requestHtml, config.popupLinks.avcForm),
        call(requestHtml, config.popupLinks.avcResult),
        call(requestHtml, config.popupLinks.moreInfo),
    ]);

    // Required because of type error with new brix version
    const contextAndFunction = {
        context: overlay,
        fn: overlay.injectContent as (...content: string[]) => void,
    };

    yield call(contextAndFunction, ...results);
    yield put(setOverlayLoaded(true));
}

export function* initProductDetails () {
    const result: ProductDetailsResponse = yield call(getProductDetails, [JarvisProductGroup.GigaTv]);

    yield put(setProductDetails(result.products.gigaTv));
}

export function* initAvailability () {
    const customer: Customer | undefined = yield select(selectCustomer);
    const filter: ProductFilter[] = yield [
        {
            group: JarvisProductGroup.GigaTv,
            includeDetails: true,
            isFastestOnly: false,
        },
        {
            group: JarvisProductGroup.TvConnect,
            includeDetails: true,
            isFastestOnly: false,
        },
    ];

    if (customer) {
        filter.push({
            group: JarvisProductGroup.TvConnectBroadVision,
            includeDetails: true,
            isFastestOnly: false,
        });
    }

    yield call(initAvailabilitySaga, filter, customer?.cableContract?.address);
}

export function* initTracking () {
    const customer: Customer | undefined = yield select(selectCustomer);
    const result: AvailabilityCheckResponse | undefined = yield select(selectAvailabilityResult);
    const isBookingDisabled: boolean = yield select(selectIsBookingDisabled);
    const isNonMonthlyBilling: boolean = yield select(selectIsNotMonthlyBilling);
    const isTvCustomer: boolean = yield select(selectIsTvCustomer);

    yield result && !customer
        ? call(trackForm, 'outcome')
        : call(trackPageView);

    if (isBookingDisabled || isNonMonthlyBilling || isTvCustomer) {
        yield call(trackCustomerNotification);
    }
}

export function* setPageQueryParams () {
    const isGigaKombi: boolean = yield select(selectIsGigakombi);
    const id: string | undefined = yield select(selectSelectedId);

    const queryData = {
        [QueryNames.PRODUCT]: id,
        [QueryNames.GIGAKOMBI]: isGigaKombi,
    };

    const query: string = yield call(createQueryString, queryData, location.search);

    yield call([history, history.replaceState], null, '', query);
}
